import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const emptyFilters = {
  user: '',
  partner_id: '',
  event_type: '',
  event_action: '',
  resource_type: '',
  resource_id: '',
  source_ip: '',
  created_after: '',
  created_before: '',
  cui_flag: '',
};

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function compact(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && String(item).trim() !== ''),
  );
}

export default function AdminActivityLogsPage(props) {
  const [filters, setFilters] = useState(emptyFilters);
  const [eventsState, setEventsState] = useState({
    loading: true,
    error: '',
    events: [],
    pagination: { limit: 25, offset: 0, total: 0 },
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cleanupState, setCleanupState] = useState({ loading: false, feedback: '', error: '' });

  async function loadEvents(nextFilters = filters, offset = 0, limit = eventsState.pagination.limit || 25) {
    setEventsState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/activity-events', {
        params: {
          ...compact(nextFilters),
          offset,
          limit,
        },
      });
      setEventsState({
        loading: false,
        error: '',
        events: response.data?.events || [],
        pagination: response.data?.pagination || { limit, offset, total: 0 },
      });
    } catch (error) {
      setEventsState((current) => ({
        ...current,
        loading: false,
        error: error?.response?.data?.message || 'Unable to load activity events.',
      }));
    }
  }

  useEffect(() => {
    loadEvents(emptyFilters, 0, 25);
  }, []);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function applyFilters(event) {
    event.preventDefault();
    loadEvents(filters, 0, eventsState.pagination.limit || 25);
  }

  function clearFilters() {
    setFilters(emptyFilters);
    loadEvents(emptyFilters, 0, eventsState.pagination.limit || 25);
  }

  async function runCleanup(dryRun) {
    setCleanupState({ loading: true, feedback: '', error: '' });
    try {
      const response = await axiosInstance.post('/admin/activity-events/cleanup', { dry_run: dryRun });
      const result = response.data?.cleanup || {};
      setCleanupState({
        loading: false,
        feedback: dryRun
          ? `${result.eligible_count || 0} events are eligible for retention cleanup.`
          : `${result.deleted_count || 0} events deleted.`,
        error: '',
      });
      if (!dryRun) {
        loadEvents(filters, 0, eventsState.pagination.limit || 25);
      }
    } catch (error) {
      setCleanupState({
        loading: false,
        feedback: '',
        error: error?.response?.data?.message || 'Unable to run activity cleanup.',
      });
    }
  }

  const page = Math.floor((eventsState.pagination.offset || 0) / (eventsState.pagination.limit || 25));
  const rowsPerPage = eventsState.pagination.limit || 25;

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[
        { label: 'AMX xConsole', href: '/' },
        { label: 'Admin Dashboard', href: '/admin' },
        { label: 'Activity Logs' },
      ]}
      title="Activity Logs"
      subtitle="Search account, admin, sharing, workspace, request, asset, and exchange-card events."
    >
      {eventsState.error ? <Alert severity="error">{eventsState.error}</Alert> : null}
      {cleanupState.feedback ? <Alert severity="success">{cleanupState.feedback}</Alert> : null}
      {cleanupState.error ? <Alert severity="error">{cleanupState.error}</Alert> : null}

      <AdminSection
        title="Filters"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => runCleanup(true)} disabled={cleanupState.loading}>
              Preview Cleanup
            </Button>
            <Button color="error" variant="outlined" onClick={() => runCleanup(false)} disabled={cleanupState.loading}>
              Delete Expired Events
            </Button>
          </Stack>
        }
      >
        <Stack component="form" spacing={2} onSubmit={applyFilters}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField label="User or Email" value={filters.user} onChange={(event) => updateFilter('user', event.target.value)} fullWidth size="small" />
            <TextField label="Partner or Business Unit" value={filters.partner_id} onChange={(event) => updateFilter('partner_id', event.target.value)} fullWidth size="small" />
            <TextField label="IP Address" value={filters.source_ip} onChange={(event) => updateFilter('source_ip', event.target.value)} fullWidth size="small" />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField label="Event Type" value={filters.event_type} onChange={(event) => updateFilter('event_type', event.target.value)} fullWidth size="small" />
            <TextField label="Action" value={filters.event_action} onChange={(event) => updateFilter('event_action', event.target.value)} fullWidth size="small" />
            <TextField label="Resource Type" value={filters.resource_type} onChange={(event) => updateFilter('resource_type', event.target.value)} fullWidth size="small" />
            <TextField label="Resource ID" value={filters.resource_id} onChange={(event) => updateFilter('resource_id', event.target.value)} fullWidth size="small" />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label="From"
              type="datetime-local"
              value={filters.created_after}
              onChange={(event) => updateFilter('created_after', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="To"
              type="datetime-local"
              value={filters.created_before}
              onChange={(event) => updateFilter('created_before', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="CUI"
              select
              value={filters.cui_flag}
              onChange={(event) => updateFilter('cui_flag', event.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button type="button" variant="text" onClick={clearFilters}>Clear</Button>
            <Button type="submit" variant="contained">Search</Button>
          </Stack>
        </Stack>
      </AdminSection>

      <AdminSection title="Events">
        {eventsState.loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography>Loading activity events...</Typography>
          </Stack>
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Partner</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>CUI</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventsState.events.map((event) => (
                    <TableRow key={event.event_id} hover>
                      <TableCell>{formatDate(event.created_at)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{event.actor_email || event.actor_user_id || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{event.actor_role || event.actor_account_type || '-'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Chip size="small" label={event.event_type} />
                          <Typography variant="body2">{event.event_action}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{event.resource_type || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{event.resource_id || '-'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{event.actor_partner_id || '-'}</TableCell>
                      <TableCell>{event.source_ip || '-'}</TableCell>
                      <TableCell>{event.cui_flag ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" size="small" onClick={() => setSelectedEvent(event)}>
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {eventsState.events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary">No activity events match the current filters.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={eventsState.pagination.total || 0}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 200]}
              onPageChange={(event, nextPage) => {
                loadEvents(filters, nextPage * rowsPerPage, rowsPerPage);
              }}
              onRowsPerPageChange={(event) => {
                loadEvents(filters, 0, Number.parseInt(event.target.value, 10));
              }}
            />
          </>
        )}
      </AdminSection>

      <Drawer anchor="right" open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)}>
        <Box sx={{ width: { xs: 360, sm: 520 }, p: 3 }}>
          {selectedEvent ? (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Event Detail</Typography>
                <Button onClick={() => setSelectedEvent(null)}>Close</Button>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="body2"><strong>ID:</strong> {selectedEvent.event_id}</Typography>
                <Typography variant="body2"><strong>Created:</strong> {formatDate(selectedEvent.created_at)}</Typography>
                <Typography variant="body2"><strong>Expires:</strong> {formatDate(selectedEvent.expires_at)}</Typography>
                <Typography variant="body2"><strong>Request:</strong> {selectedEvent.http_method || '-'} {selectedEvent.path || '-'}</Typography>
                <Typography variant="body2"><strong>Request ID:</strong> {selectedEvent.request_id || '-'}</Typography>
                <Typography variant="body2"><strong>User Agent:</strong> {selectedEvent.user_agent || '-'}</Typography>
              </Stack>
              <Divider />
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default',
                  overflow: 'auto',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(selectedEvent.metadata || {}, null, 2)}
              </Box>
            </Stack>
          ) : null}
        </Box>
      </Drawer>
    </AdminLayout>
  );
}
